import CustomButton from '@/components/atoms/CustomButton';
import MarkdownEditor from '@/components/organisms/MarkdownEditor/MarkdownEditor';
import LoadingPage from '@/components/templates/LoadingPage';
import { useLegalDocumentById, useUpdateLegalDocument } from '@/hooks/legal/legal';
import EulaActivationConfirmModal from '@/modules/legal/eula/EulaActivationConfirmModal';
import EulaDocPreview from '@/modules/legal/eula/EulaDocPreview';
import { LEGAL_DOCUMENT_TYPE_LABELS } from '@/constants/stringVars';
import { TLegalDocument, TLegalDocumentType } from '@/types/Legal.types';
import { Badge, Box, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { isUndefined } from 'lodash';
import { useEffect, useState } from 'react';

const typeLabel = (type: TLegalDocument['type']) =>
  LEGAL_DOCUMENT_TYPE_LABELS[type as TLegalDocumentType] ?? type?.replaceAll('_', ' ');

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : format(date, 'MMM d, yyyy · HH:mm');
};

const EulaDocument = () => {
  const { id: documentId } = Route.useParams();
  const { isLoading, data, isSuccess } = useLegalDocumentById(documentId);
  const { isPending, mutate } = useUpdateLegalDocument();

  const { isOpen: isActivateModalOpen, onClose: onActivateModalClose, onOpen: onActivateModalOpen } = useDisclosure();

  const [markdownEditorValue, setMarkdownEditorValue] = useState<string | undefined>(data?.content);

  useEffect(() => {
    if (data && isSuccess) {
      setMarkdownEditorValue(data.content);
    }
  }, [data, isSuccess]);

  const hasUnsavedChanges = markdownEditorValue !== data?.content;

  const onSaveChanges = () => {
    if (isUndefined(data)) return;
    mutate({
      id: documentId,
      isActive: data?.isActive,
      content: markdownEditorValue ?? data?.content,
      type: data?.type,
      version: data?.version,
      s3Key: data?.s3Key,
    });
  };

  const onActivateDeactivate = () => {
    onActivateModalOpen();
  };

  if (isLoading) return <LoadingPage />;

  return (
    <VStack width={'100%'} alignItems={'stretch'} spacing={'16px'}>
      {isSuccess && !isUndefined(data) && (
        <>
          {/* Header: title, status, version, dates + actions */}
          <HStack justifyContent={'space-between'} alignItems={'flex-start'} width={'100%'}>
            <VStack alignItems={'flex-start'} spacing={'6px'}>
              <HStack spacing={'12px'} alignItems={'center'}>
                <Text variant="loraSectionTitle">{typeLabel(data.type)}</Text>
                <Badge colorScheme={data.isActive ? 'green' : 'gray'}>
                  {data.isActive ? 'Active' : 'Inactive'}
                </Badge>
                <Text fontSize={'14px'} color={'text.mediumGray'}>
                  Version {data.version}
                </Text>
              </HStack>
              <Text fontSize={'12px'} color={'text.mediumGray'}>
                Created {formatDate(data.createdAt)} · Updated {formatDate(data.updatedAt)}
              </Text>
            </VStack>
            <VStack alignItems={'flex-end'} spacing={'6px'}>
              <HStack gap={'12px'}>
                <CustomButton
                  backgroundColor={'primary.500'}
                  label="Save Changes"
                  onClick={onSaveChanges}
                  disabled={!hasUnsavedChanges}
                  isLoading={isPending}
                />
                <CustomButton
                  backgroundColor={data.isActive ? 'red' : 'secondary.500'}
                  disabled={hasUnsavedChanges}
                  label={`${data.isActive ? 'Deactivate' : 'Activate'} Document`}
                  onClick={onActivateDeactivate}
                />
              </HStack>
              {hasUnsavedChanges && (
                <Text fontSize={'11px'} color={'text.mediumGray'}>
                  Save changes to enable activation.
                </Text>
              )}
            </VStack>
          </HStack>

          {/* Editor + live preview, side by side */}
          <HStack alignItems={'stretch'} width={'100%'} spacing={'20px'}>
            <VStack flex={1} alignItems={'stretch'} spacing={'8px'}>
              <Text fontSize={'12px'} fontWeight={'semibold'} color={'text.mediumGray'}>
                EDIT
              </Text>
              <VStack height={'70vh'} width={'100%'}>
                <MarkdownEditor content={markdownEditorValue ?? ''} setContent={setMarkdownEditorValue} />
              </VStack>
            </VStack>
            <VStack flex={1} alignItems={'stretch'} spacing={'8px'}>
              <Text fontSize={'12px'} fontWeight={'semibold'} color={'text.mediumGray'}>
                PREVIEW
              </Text>
              <Box height={'70vh'} width={'100%'} overflowY={'auto'}>
                <EulaDocPreview content={markdownEditorValue ?? ''} />
              </Box>
            </VStack>
          </HStack>

          <Text fontSize={'11px'} color={'text.mediumGray'}>
            S3 key: {data.s3Key}
          </Text>

          <EulaActivationConfirmModal document={data} isOpen={isActivateModalOpen} onClose={onActivateModalClose} />
        </>
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/eula/$id')({
  component: EulaDocument,
});
